import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Settings } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      const savedPrefs = JSON.parse(consent);
      setPreferences(savedPrefs);
      applyCookiePreferences(savedPrefs);
    }
  }, []);

  const applyCookiePreferences = (prefs) => {
    // Analytics cookies
    if (prefs.analytics) {
      // Google Analytics is already loaded in index.html, just track consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      }
    } else {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        });
      }
    }

    // Marketing cookies
    if (prefs.marketing) {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted'
        });
      }
    } else {
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      }
    }
  };

  const handleAcceptAll = () => {
    const newPrefs = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(newPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(newPrefs));
    applyCookiePreferences(newPrefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptNecessary = () => {
    const newPrefs = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(newPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(newPrefs));
    applyCookiePreferences(newPrefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    applyCookiePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
      <div className="bg-white border-t-4 border-[#FF5A3D] shadow-2xl">
        <div className="max-w-7xl mx-auto p-6">
          {!showSettings ? (
            // Main Cookie Banner
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start space-x-4 flex-1">
                <Cookie size={32} className="text-[#FF5A3D] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-[#0E163B] text-lg mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. Read our{' '}
                    <a href="/privacy" className="text-[#FF5A3D] underline hover:text-[#E5512E]">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/terms" className="text-[#FF5A3D] underline hover:text-[#E5512E]">
                      Cookie Policy
                    </a>{' '}
                    for more information.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                >
                  <Settings size={16} className="mr-2" />
                  Cookie Settings
                </Button>
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  className="border-2 border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white whitespace-nowrap"
                >
                  Necessary Only
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold whitespace-nowrap"
                >
                  Accept All Cookies
                </Button>
              </div>
            </div>
          ) : (
            // Cookie Settings Panel
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-[#0E163B] text-xl flex items-center">
                  <Settings size={24} className="mr-2 text-[#FF5A3D]" />
                  Cookie Preferences
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={preferences.necessary}
                        disabled
                        className="w-5 h-5 text-[#FF5A3D] rounded"
                      />
                      <span className="font-semibold text-[#0E163B]">Necessary Cookies</span>
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded">Always Active</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">
                    These cookies are essential for the website to function properly. They enable core functionality such as security, 
                    network management, and accessibility. You cannot disable these cookies.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => togglePreference('analytics')}
                        className="w-5 h-5 text-[#FF5A3D] rounded cursor-pointer"
                      />
                      <span className="font-semibold text-[#0E163B]">Analytics Cookies</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. 
                    This helps us improve our website and services.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => togglePreference('marketing')}
                        className="w-5 h-5 text-[#FF5A3D] rounded cursor-pointer"
                      />
                      <span className="font-semibold text-[#0E163B]">Marketing Cookies</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 ml-8">
                    These cookies are used to track visitors across websites and display relevant advertisements. They may be set by us or 
                    third-party providers to build a profile of your interests.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Accept Necessary Only
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-semibold"
                >
                  Save My Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
